import React, { useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import { SubnavOption } from 'src/components/Subnav/Subnav'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useExtendLocation } from 'src/hooks/locationHooks'
import { useTranslate } from 'src/localization/Trns'
import { AppLocation } from 'src/navigation/appLocations'
import ExpenseRequirementsAppRoute from 'src/navigation/appRoutes/organization/expenseRequirements.appRoute'
import { Rdrct } from 'src/navigation/Lnk'
import { expenseRequirementsRouteProps } from 'src/navigation/routeProps'
import { expenseRequirementsReceiptRulesUrl } from 'src/navigation/urls'
import { selectOrganizationIdFromRoute } from 'src/redux/selectors/data/organizations.selectors'
import { selectLoadedFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

const ExpenseRequirements: React.FC<React.PropsWithChildren<unknown>> = () => {
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )

  const translate = useTranslate()
  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const extendLocation = useExtendLocation()
  const currentUser = useCurrentUser()
  const loadedRemoteConfig = useSelector(selectLoadedFromRemoteConfigState)
  const appName = useSelector(selectAppName)

  const [addingNewField, setAddingNewField] = useState<boolean>(false)

  const expenseRequirementsSubnavOptions = useMemo(
    (): SubnavOption[] => [
      {
        label: translate('Receipt Rules'),
        to: expenseRequirementsReceiptRulesUrl(
          organizationId !== undefined ? organizationId : '',
        ),
        isActive:
          extendLocation.location ===
          AppLocation.OrganizationSettingsExpenseRequirementsReceiptRules,
        ariaLabelSource: translate('Receipt Rules'),
        'data-testid': 'subnavExpenseRequirementsReceiptRules',
      },
    ],
    [extendLocation, organizationId, translate],
  )

  if (!loadedRemoteConfig) {
    return <LoadingBackgroundPage />
  }

  if (!currentUser?.hasOrgBookkeeperPermissions) {
    return <NotFoundBackgroundPage />
  }

  return (
    <>
      <Helmet>
        <title>{`Expense Requirements | ${appName}`}</title>
        <meta
          name="description"
          content={`Expense Requirements page of ${appName}`}
        />
      </Helmet>
      <LayoutPage
        subnavOptions={expenseRequirementsSubnavOptions}
        title={<span>{translate('Expense Requirements')}</span>}
      >
        <Switch location={calculatedBackgroundLocation}>
          {ExpenseRequirementsAppRoute.subRoutes.map((route, i) => (
            <Route key={i} {...route.routeProps}>
              {route.component &&
                React.cloneElement(route.component, {
                  addingNewField,
                  setAddingNewField,
                })}
            </Route>
          ))}
          <Route {...expenseRequirementsRouteProps} exact>
            <Rdrct
              to={expenseRequirementsReceiptRulesUrl(
                organizationId !== undefined ? organizationId : '',
              )}
            />
          </Route>
          <Route path={'/'}>
            <NotFoundBackgroundPageContent />
          </Route>
        </Switch>
      </LayoutPage>
    </>
  )
}

export default ExpenseRequirements
